// Phase 6.1 확장 · 판매자 프로필 홈 설정
// 대표 커버 · 한 줄 소개
// (Phase 6.1 HOTFIX · 방송상품 노출 → LIVE 판매 콘솔 / 전체상품 노출 → 상품 관리 로 이동됨)
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import api from '@/lib/api';

// STEP OVERLAY-2C-UI-COMPLETE · Seller DEFAULT Overlay 기본값.
// 신규 API 금지 — 기존 /seller/settings REUSE.
const DEFAULT_OVERLAY = {
  show_image: true, show_name: true, show_price: true,
  show_options: false, show_size: false, show_option_price: false,
};
const OVERLAY_FIELDS = [
  ['show_image',        '상품 이미지'],
  ['show_name',         '상품명'],
  ['show_price',        '판매가'],
  ['show_options',      '옵션'],
  ['show_size',         '사이즈'],
  ['show_option_price', '옵션별 단가'],
];

export default function SellerProfileHomeSettings({ user }) {
  const nav = useNavigate();
  const [cfg, setCfg] = useState({ cover_image_url: '', tagline: '' });
  const [overlay, setOverlay] = useState(DEFAULT_OVERLAY);
  const [followerAlert, setFollowerAlert] = useState({ enabled: true, duration_ms: 4000 });
  // STEP OVERLAY-2H-PURCHASE-WIRE · 구매 알림 settings (REUSE /seller/settings).
  const [purchaseAlert, setPurchaseAlert] = useState({ enabled: true, duration_ms: 4000 });
  const [seller, setSeller] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingOverlay, setSavingOverlay] = useState(false);
  const [savingFollower, setSavingFollower] = useState(false);
  const [savingPurchase, setSavingPurchase] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/seller/profile-home-config'),
      api.get('/seller/me'),
      api.get('/seller/settings').catch(() => ({ data: {} })),
    ]).then(([r, s, ss]) => {
      setCfg({ cover_image_url: r.data?.cover_image_url || '', tagline: r.data?.tagline || '' });
      setSeller(s.data);
      const o = ss?.data?.overlay_default_preset;
      if (o && typeof o === 'object') setOverlay({ ...DEFAULT_OVERLAY, ...o });
      // STEP OVERLAY-2G-FOLLOWER · load follower alert settings
      setFollowerAlert({
        enabled: ss?.data?.follower_alert_enabled !== false,
        duration_ms: parseInt(ss?.data?.follower_alert_duration_ms || 4000, 10),
      });
      // STEP OVERLAY-2H-PURCHASE-WIRE · load purchase alert settings
      setPurchaseAlert({
        enabled: ss?.data?.purchase_alert_enabled !== false,
        duration_ms: parseInt(ss?.data?.purchase_alert_duration_ms || 4000, 10),
      });
    }).catch(() => toast.error('설정을 불러오지 못했습니다.'));
  }, []);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const toggleOverlay = async (k) => {
    const next = { ...overlay, [k]: !overlay[k] };
    setOverlay(next); setSavingOverlay(true);
    try { await api.put('/seller/settings', { overlay_default_preset: next }); }
    catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); setOverlay(overlay); }
    finally { setSavingOverlay(false); }
  };
  const toggleFollower = async (k, v) => {
    const next = { ...followerAlert, [k]: v };
    setFollowerAlert(next); setSavingFollower(true);
    try {
      await api.put('/seller/settings', {
        follower_alert_enabled: next.enabled,
        follower_alert_duration_ms: next.duration_ms,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); setFollowerAlert(followerAlert); }
    finally { setSavingFollower(false); }
  };
  // STEP OVERLAY-2H-PURCHASE-WIRE · 구매 알림 toggle (follower 와 동일 패턴 REUSE).
  const togglePurchase = async (k, v) => {
    const next = { ...purchaseAlert, [k]: v };
    setPurchaseAlert(next); setSavingPurchase(true);
    try {
      await api.put('/seller/settings', {
        purchase_alert_enabled: next.enabled,
        purchase_alert_duration_ms: next.duration_ms,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); setPurchaseAlert(purchaseAlert); }
    finally { setSavingPurchase(false); }
  };
  const save = async () => {
    setSaving(true);
    try { await api.patch('/seller/profile-home-config', cfg); toast.success('저장되었습니다.'); }
    catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="profile-home-settings-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <button onClick={() => nav('/seller/dashboard')} className="inline-flex items-center gap-1 text-sm font-bold"><ChevronLeft size={16}/> 대시보드</button>
          {seller && (
            <a href={`/seller/${seller.seller_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black" data-testid="settings-view-profile">
              <ExternalLink size={11}/> 내 프로필 보기
            </a>
          )}
        </div>

        <div>
          <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>🎨 프로필 홈 설정</h1>
          <p className="text-xs text-gray-500 mt-1">판매자 프로필을 나만의 브랜드홈으로 꾸미세요.</p>
        </div>

        {/* 대표 커버 */}
        <section className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-5 space-y-3">
          <h2 className="text-base font-black">📸 대표 커버 이미지</h2>
          <input type="text" placeholder="이미지 URL (예: https://…)" value={cfg.cover_image_url}
            onChange={(e) => set('cover_image_url', e.target.value)}
            className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm"
            data-testid="settings-cover-input"/>
          {cfg.cover_image_url && (
            <div className="relative aspect-[16/6] rounded-lg overflow-hidden bg-gray-100">
              <img src={cfg.cover_image_url} alt="cover preview" className="absolute inset-0 w-full h-full object-cover"/>
            </div>
          )}
          <p className="text-[11px] text-gray-500">권장 비율 16:6 · 최소 1200px 너비. 프로필 상단 배경으로 사용됩니다.</p>
        </section>

        {/* 한 줄 소개 */}
        <section className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-5 space-y-3">
          <h2 className="text-base font-black">✍️ 한 줄 소개 (tagline)</h2>
          <input type="text" placeholder="예: 여성패션 전문 · 매주 화/목 LIVE · 신상품 매주 업데이트" value={cfg.tagline}
            onChange={(e) => set('tagline', e.target.value.slice(0, 120))}
            className="w-full px-3 py-2 rounded border border-black/10 bg-transparent text-sm"
            data-testid="settings-tagline-input"/>
          <p className="text-[11px] text-gray-500">{cfg.tagline.length}/120자 · 프로필 상단 1줄로 표시됩니다.</p>
        </section>

        {/* STEP OVERLAY-2C-UI-COMPLETE · Seller DEFAULT Overlay 표시 기본값 */}
        <section className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-5 space-y-3"
                 data-testid="settings-overlay-default-section">
          <h2 className="text-base font-black">📺 라이브 상품 Overlay 기본값</h2>
          <p className="text-[11px] text-gray-500">
            <b>상품 올리기</b> 를 눌렀을 때 영상 위 Overlay 에 기본으로 표시할 항목이에요.
            <br/>· <b>DEFAULT</b> = 이 설정 · <b>FULL</b> = 상품에 실제 존재하는 공개 가능 정보 전체 (이 설정과 무관)
            <br/>· 실제 데이터가 없는 항목은 자동으로 숨겨져요 (임의 생성 X).
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {OVERLAY_FIELDS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                     data-testid={`settings-overlay-${k}-row`}>
                <input type="checkbox" checked={!!overlay[k]}
                       disabled={savingOverlay}
                       onChange={() => toggleOverlay(k)}
                       data-testid={`settings-overlay-${k}`}/>
                <span className="text-sm font-bold">{label}</span>
              </label>
            ))}
          </div>
          <p className="text-[10px] text-gray-400">
            {savingOverlay ? '저장 중…' : '변경 즉시 저장됩니다.'}
          </p>
        </section>

        {/* STEP OVERLAY-2G-FOLLOWER · 단골 알림 설정 */}
        <section className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-5 space-y-3"
                 data-testid="settings-follower-alert-section">
          <h2 className="text-base font-black">❤️ LIVE 단골 알림</h2>
          <p className="text-[11px] text-gray-500">
            방송 중 새 단골이 생기면 영상 Overlay 에 짧게 알림을 띄워요.
            <br/>· <b>OFF</b> 로 해도 단골 기능 자체는 정상 작동합니다 (단골 수 · 그래프 모두 동일).
          </p>
          <label className="flex items-center gap-3 cursor-pointer"
                 data-testid="settings-follower-alert-row">
            <input type="checkbox" checked={followerAlert.enabled !== false}
                   disabled={savingFollower}
                   onChange={() => toggleFollower('enabled', !(followerAlert.enabled !== false))}
                   data-testid="settings-follower-alert-enabled"/>
            <span className="text-sm font-bold">새 단골 알림 표시</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-600 dark:text-gray-300">표시 시간</span>
            <input type="number" min={2} max={10} step={0.5}
                   value={(followerAlert.duration_ms || 4000) / 1000}
                   disabled={savingFollower || followerAlert.enabled === false}
                   onChange={(e) => toggleFollower('duration_ms',
                       Math.round(Math.max(2, Math.min(10, Number(e.target.value) || 4)) * 1000))}
                   className="px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent text-[11px] font-bold w-16"
                   data-testid="settings-follower-alert-duration"/>
            <span className="text-[11px] text-gray-500">초</span>
          </div>
          <p className="text-[10px] text-gray-400">{savingFollower ? '저장 중…' : '변경 즉시 저장됩니다.'}</p>
        </section>

        {/* STEP OVERLAY-2H-PURCHASE-WIRE · 구매 알림 설정 */}
        <section className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-5 space-y-3"
                 data-testid="settings-purchase-alert-section">
          <h2 className="text-base font-black">🎉 LIVE 구매 알림</h2>
          <p className="text-[11px] text-gray-500">
            방송 중 실제 주문이 생성/확정되면 영상 Overlay 에 짧게 알림을 띄워요.
            <br/>· <b>OFF</b> 로 해도 주문 · 결제 · 재고 · OMS 는 정상 작동합니다.
            <br/>· 단순 장바구니 추가나 댓글주문 "후보" 단계는 알림 대상이 아니에요.
          </p>
          <label className="flex items-center gap-3 cursor-pointer"
                 data-testid="settings-purchase-alert-row">
            <input type="checkbox" checked={purchaseAlert.enabled !== false}
                   disabled={savingPurchase}
                   onChange={() => togglePurchase('enabled', !(purchaseAlert.enabled !== false))}
                   data-testid="settings-purchase-alert-enabled"/>
            <span className="text-sm font-bold">구매 알림 표시</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-600 dark:text-gray-300">표시 시간</span>
            <input type="number" min={2} max={10} step={0.5}
                   value={(purchaseAlert.duration_ms || 4000) / 1000}
                   disabled={savingPurchase || purchaseAlert.enabled === false}
                   onChange={(e) => togglePurchase('duration_ms',
                       Math.round(Math.max(2, Math.min(10, Number(e.target.value) || 4)) * 1000))}
                   className="px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent text-[11px] font-bold w-16"
                   data-testid="settings-purchase-alert-duration"/>
            <span className="text-[11px] text-gray-500">초</span>
          </div>
          <p className="text-[10px] text-gray-400">{savingPurchase ? '저장 중…' : '변경 즉시 저장됩니다.'}</p>
        </section>

        {/* 안내 · 이동 안내 */}
        <section className="rounded-2xl border border-dashed border-black/15 dark:border-white/15 p-4 text-[12px] text-gray-600 dark:text-gray-300 leading-relaxed" data-testid="settings-relocation-notice">
          📌 <b>방송 중 판매상품 노출</b>은 <b>LIVE 판매 콘솔</b>에서,
          <b> 전체상품 노출</b> 설정은 <b>상품 관리</b> 메뉴에서 변경할 수 있어요.
        </section>

        <div className="sticky bottom-4 flex justify-end">
          <button onClick={save} disabled={saving}
            className="px-6 py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-50 text-white font-black text-sm shadow-lg"
            data-testid="settings-save">
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
